import { Application, Container, Point, Rectangle } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';

export class IsometricCamera {
  public readonly container: Container;
  public readonly viewport: Rectangle;
  public zoom = 1;
  public position: Point = new Point(0, 0);

  private readonly app: Application;
  
  // Camera movement properties
  private moveSpeed = 5;
  private zoomSpeed = 0.1;
  private minZoom = 0.25;
  private maxZoom = 3;
  
  // Input state tracking
  private keysPressed = new Set<string>();

  constructor(app: Application) {
    this.app = app;
    this.container = new Container();
    this.viewport = app.screen.clone();
    this.container.sortableChildren = true;
  }

  public screenToWorld(screenPos: Point): Point {
    const worldX = (screenPos.x - this.position.x) / this.zoom;
    const worldY = (screenPos.y - this.position.y) / this.zoom;
    return new Point(worldX, worldY);
  }

  public worldToScreen(worldPos: Point): Point {
    const screenX = worldPos.x * this.zoom + this.position.x;
    const screenY = worldPos.y * this.zoom + this.position.y;
    return new Point(screenX, screenY);
  }

  public screenToGrid(screenPos: Point): Point {
    const worldPos = this.screenToWorld(screenPos);
    return this.worldToGrid(worldPos);
  }

  public gridToScreen(gridPos: Point): Point {
    const worldPos = this.gridToWorld(gridPos);
    return this.worldToScreen(worldPos);
  }

  public pan(delta: Point): void {
    this.position.x += delta.x;
    this.position.y += delta.y;
    this.updateTransform();
  }

  public zoomBy(factor: number): void {
    const newZoom = this.zoom * factor;
    this.zoom = Math.min(Math.max(newZoom, this.minZoom), this.maxZoom);
    this.updateTransform();
  }

  public setZoom(value: number): void {
    this.zoom = Math.min(Math.max(value, this.minZoom), this.maxZoom);
    this.updateTransform();
  }

  // Camera movement methods
  public update(deltaTime: number): void {
    this.handleMovement(deltaTime);
  }

  public onKeyDown(event: KeyboardEvent): void {
    this.keysPressed.add(event.code.toLowerCase());
  }

  public onKeyUp(event: KeyboardEvent): void {
    this.keysPressed.delete(event.code.toLowerCase());
  }

  public onWheel(event: WheelEvent): void {
    if (!event || typeof event.deltaY !== 'number') {
      return;
    }
    
    const zoomDelta = event.deltaY > 0 ? -this.zoomSpeed : this.zoomSpeed;
    this.zoomBy(1 + zoomDelta);
  }

  private handleMovement(deltaTime: number): void {
    const moveDistance = this.moveSpeed * deltaTime;
    let deltaX = 0;
    let deltaY = 0;

    // WASD movement - simple screen-relative directions
    // Camera moves opposite to the direction you want to look
    if (this.keysPressed.has('keyw')) {
      // W: Look up on screen (camera moves down to show more above)
      deltaY += moveDistance;
    }
    if (this.keysPressed.has('keys')) {
      // S: Look down on screen (camera moves up to show more below)
      deltaY -= moveDistance;
    }
    if (this.keysPressed.has('keya')) {
      // A: Look left on screen (camera moves right to show more left)
      deltaX += moveDistance;
    }
    if (this.keysPressed.has('keyd')) {
      // D: Look right on screen (camera moves left to show more right)
      deltaX -= moveDistance;
    }

    if (deltaX !== 0 || deltaY !== 0) {
      this.pan(new Point(deltaX, deltaY));
    }
  }

  private updateTransform(): void {
    this.container.position.copyFrom(this.position);
    this.container.scale.set(this.zoom);
    // Don't force immediate render - let the normal render loop handle it
    // this.app.renderer.render(this.container);
  }

  private gridToWorld(gridPos: Point): Point {
    return new Point(
      (gridPos.x - gridPos.y) * (GridSystem.TILE_WIDTH / 2),
      (gridPos.x + gridPos.y) * (GridSystem.TILE_HEIGHT / 2)
    );
  }

  private worldToGrid(worldPos: Point): Point {
    const x =
      (worldPos.x / (GridSystem.TILE_WIDTH / 2) +
        worldPos.y / (GridSystem.TILE_HEIGHT / 2)) /
      2;
    const y =
      (worldPos.y / (GridSystem.TILE_HEIGHT / 2) -
        worldPos.x / (GridSystem.TILE_WIDTH / 2)) /
      2;

    const rounded = new Point(Math.round(x), Math.round(y));
    const snapped = this.snapToContainingTile(worldPos, rounded);
    return snapped;
  }

  private snapToContainingTile(worldPos: Point, candidate: Point): Point {
    if (this.isPointInTile(worldPos, candidate.x, candidate.y)) {
      return candidate;
    }

    const neighbors: Point[] = [
      new Point(candidate.x - 1, candidate.y),
      new Point(candidate.x + 1, candidate.y),
      new Point(candidate.x, candidate.y - 1),
      new Point(candidate.x, candidate.y + 1),
      new Point(candidate.x - 1, candidate.y - 1),
      new Point(candidate.x - 1, candidate.y + 1),
      new Point(candidate.x + 1, candidate.y - 1),
      new Point(candidate.x + 1, candidate.y + 1)
    ];

    for (const neighbor of neighbors) {
      if (this.isPointInTile(worldPos, neighbor.x, neighbor.y)) {
        return neighbor;
      }
    }

    return candidate;
  }

  private isPointInTile(worldPos: Point, gridX: number, gridY: number): boolean {
    const corners = this.getTileCorners(gridX, gridY);
    const vertices = [corners.top, corners.right, corners.bottom, corners.left];

    let sign = 0;
    for (let i = 0; i < vertices.length; i += 1) {
      const a = vertices[i];
      const b = vertices[(i + 1) % vertices.length];
      const cross = (b.x - a.x) * (worldPos.y - a.y) - (b.y - a.y) * (worldPos.x - a.x);

      if (Math.abs(cross) < 0.001) {
        continue;
      }

      const currentSign = Math.sign(cross);
      if (sign === 0) {
        sign = currentSign;
      } else if (currentSign !== sign) {
        return false;
      }
    }

    return true;
  }

  private getTileCorners(gridX: number, gridY: number): {
    top: Point;
    right: Point;
    bottom: Point;
    left: Point;
  } {
    const base = this.gridToWorld(new Point(gridX, gridY));
    return {
      top: new Point(base.x, base.y - GridSystem.TILE_HEIGHT),
      right: new Point(
        base.x + GridSystem.TILE_WIDTH / 2,
        base.y - GridSystem.TILE_HEIGHT / 2
      ),
      bottom: new Point(base.x, base.y),
      left: new Point(
        base.x - GridSystem.TILE_WIDTH / 2,
        base.y - GridSystem.TILE_HEIGHT / 2
      )
    };
  }
}
